import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchBundle } from '../../utils/zstack/bundle';

/*
 * A Z-stack in the photo viewer (components/PhotoViewer.js;
 * docs/FIELD_AI_VISION_SPEC.md 10.3): the "Z-stack · 12 layers" badge, a
 * slider through the layers -- the arrow keys move it while it has the focus
 * -- and the stack's manifest with its layers' addresses to download, for
 * stacking software. The bundle is read as GET /media-bundles/{id} answers
 * this reader: only the layers they may see. Nothing is shown while it loads
 * or when it cannot be read.
 *
 *   bundleId   the photograph's or micrograph's bundle_id
 *   mediaId    its own id: the slider starts on it
 *   onLayer    (url | null): the layer to show instead of the photograph, null
 *              for the photograph itself
 */
export default function BundleLayers({ bundleId, mediaId, onLayer, testId = 'bundle-layers' }) {
  const { t } = useTranslation('camera');
  const [bundle, setBundle] = useState(null);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    let alive = true;
    setBundle(null);
    onLayer?.(null);
    fetchBundle(bundleId).then((answer) => {
      if (!alive || !answer?.members?.length) return;
      const own = answer.members.findIndex(m => (m.photo_id || m.microscopy_image_id) === mediaId);
      setBundle(answer);
      setIndex(own >= 0 ? own : 0);
    }).catch(() => {});
    return () => { alive = false; };
    // onLayer is the viewer's state setter: stable.
  }, [bundleId, mediaId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!bundle) return null;
  const count = bundle.members.length;
  const choose = (i) => {
    setIndex(i);
    const member = bundle.members[i];
    onLayer?.((member.photo_id || member.microscopy_image_id) === mediaId ? null : member.url);
  };
  const download = () => {
    if (typeof URL.createObjectURL !== 'function') return;
    const file = new Blob([JSON.stringify({
      bundle_id: bundle.bundle_id, kind: bundle.kind, cover_position: bundle.cover_position, manifest: bundle.manifest,
      layers: bundle.members.map(m => ({ position: m.position, url: m.url, focus_distance: m.focus_distance,
                                         sharpness: m.sharpness, t_ms: m.t_ms })),
    }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = `zstack-${bundle.bundle_id}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <div data-testid={testId} data-layers={count} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span data-testid={`${testId}-badge`} style={badge}>{t('zstack.badge', { count })}</span>
      <input type="range" min={0} max={count - 1} step={1} value={index} data-testid={`${testId}-scrubber`}
             aria-label={t('zstack.scrubber')} aria-valuetext={t('zstack.scrubberValue', { n: index + 1, count })}
             onChange={e => choose(Number(e.target.value))} style={{ flex: '1 1 120px', minWidth: 100 }} />
      <bdi dir="ltr" style={{ fontSize: 12 }} data-testid={`${testId}-position`}>{`${index + 1}/${count}`}</bdi>
      <button type="button" onClick={download} data-testid={`${testId}-download`} style={link}>
        {t('zstack.download')}
      </button>
    </div>
  );
}

const badge = { fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: 'rgba(255,255,255,0.18)' };
const link = { background: 'none', border: 'none', padding: 0, color: 'var(--c-primary-border-4-fg)', cursor: 'pointer',
               fontSize: 12, textDecoration: 'underline' };
